// Chat API Service for ConnectAndGo
// Integrates with backend chat endpoints

// auth is imported via the shared getAuthHeaders function
import {
  ConversationListItem,
  ConversationMessagesResponse,
  CreateConversationResponse,
  SendMessageRequest,
  SendMessageResponse,
  ChatStats,
  ConversationsResponse,
  ChatError,
  FirebaseConfigResponse,
  FirebaseConfig
} from '../types/Chat';

// Interface for raw conversation data from backend API
interface RawConversationData {
  firebase_conversation_id?: string;
  conversation_id?: string;
  firebase_config?: FirebaseConfig;
  conversation_type?: 'activity' | 'profile_dm';
  activity: {
    id: number;
    title: string;
    date: string;
    time: string;
    location: string;
    image_url?: string;
  } | null;
  other_participant: {
    user_id: string;
    name: string;
    email: string;
    profile_picture?: string;
    role: 'participant' | 'organizer' | 'member';
  };
  last_message?: {
    text: string;
    sender_name: string;
    timestamp: string;
  };
  unread_count?: number;
  status: string;
  user_role?: string;
  created_at: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Get authentication headers for API requests (robust: idToken with accessToken fallback)
// Import the shared getAuthHeaders from api.ts to use consistent token handling
import { getAuthHeaders as getSharedAuthHeaders } from './api';

const getAuthHeaders = async (): Promise<HeadersInit> => {
  // Use the shared auth headers logic that has rate limiting and caching
  const sharedHeaders = await getSharedAuthHeaders();
  
  // Add Accept header for chat API
  return {
    ...sharedHeaders,
    'Accept': 'application/json',
  };
};

// Utility: check if Authorization header exists in HeadersInit
const hasAuthorization = (headers: HeadersInit): boolean => {
  if (headers instanceof Headers) {
    return headers.has('Authorization');
  }
  if (Array.isArray(headers)) {
    return headers.some(([k]) => k.toLowerCase() === 'authorization');
  }
  const rec = headers as Record<string, string>;
  return !!rec['Authorization'];
};

// Custom error class for chat-specific errors
export class ChatApiError extends Error {
  public type: ChatError['type'];
  public conversationId?: string;

  constructor(type: ChatError['type'], message: string, conversationId?: string) {
    super(message);
    this.name = 'ChatApiError';
    this.type = type;
    this.conversationId = conversationId;
  }
}

/**
 * Get Firebase configuration from backend
 */
export const getFirebaseConfig = async (): Promise<FirebaseConfig> => {
  console.log('🔄 Fetching Firebase configuration from backend');
  
  try {
    const headers = await getAuthHeaders();
    if (!hasAuthorization(headers)) {
      throw new ChatApiError('ACCESS_DENIED', 'User not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/chat/firebase-config/`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new ChatApiError('CONNECTION_ERROR', `Failed to get Firebase config: ${response.status}`);
    }

    const data: FirebaseConfigResponse = await response.json();
    console.log('✅ Firebase configuration retrieved successfully');
    return data.firebase_config;

  } catch (error) {
    console.error('Error getting Firebase config:', error);
    if (error instanceof ChatApiError) {
      throw error;
    }
    throw new ChatApiError('CONNECTION_ERROR', 'Network error while getting Firebase config');
  }
};

const DEFAULT_ACTIVITY_MESSAGE = "Hi! I'm interested in this activity.";
const DEFAULT_PROFILE_MESSAGE = "Hi! I'd like to connect.";

/**
 * Create a conversation: activity-scoped (organizer) or profile DM (recipient Firebase UID).
 * Profile flow POST body: { recipient_user_id, initial_message } — see docs/social/CHAT_PROFILE_AND_ACTIVITY_CONVERSATIONS.md
 */
export async function createConversation(
  activityId: number,
  initialMessage?: string
): Promise<CreateConversationResponse>;
export async function createConversation(options: {
  recipientUserId: string;
  initialMessage?: string;
}): Promise<CreateConversationResponse>;
export async function createConversation(
  first: number | { recipientUserId: string; initialMessage?: string },
  initialMessage?: string
): Promise<CreateConversationResponse> {
  const isProfile = typeof first === 'object' && first !== null && 'recipientUserId' in first;

  const body: Record<string, unknown> = isProfile
    ? {
        recipient_user_id: first.recipientUserId,
        initial_message:
          first.initialMessage?.trim() || DEFAULT_PROFILE_MESSAGE,
      }
    : {
        activity_id: first as number,
        initial_message: (initialMessage ?? DEFAULT_ACTIVITY_MESSAGE).trim() || DEFAULT_ACTIVITY_MESSAGE,
      };

  console.log(
    '🔄 Creating conversation:',
    isProfile ? 'profile' : 'activity',
    isProfile ? { recipient_user_id: (body as { recipient_user_id: string }).recipient_user_id } : { activity_id: body.activity_id }
  );

  try {
    const headers = await getAuthHeaders();
    if (!hasAuthorization(headers)) {
      throw new ChatApiError('ACCESS_DENIED', 'User not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/chat/conversations/create/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorData: Record<string, unknown> = {};
      try {
        errorData = await response.json();
      } catch {
        // non-JSON error body
      }

      const errStr = (() => {
        const e = errorData.error ?? errorData.detail ?? errorData.message;
        if (typeof e === 'string') return e;
        if (Array.isArray(e) && typeof e[0] === 'string') return e[0];
        return '';
      })();

      if (response.status === 403) {
        throw new ChatApiError(
          'SUBSCRIPTION_REQUIRED',
          errStr || 'Subscription required to start this conversation'
        );
      }

      if (response.status === 400) {
        if (errStr.includes('yourself')) {
          throw new ChatApiError('ACCESS_DENIED', 'Cannot create conversation with yourself');
        }
        throw new ChatApiError(
          'CONNECTION_ERROR',
          errStr || `Invalid request (${response.status})`
        );
      }

      if (response.status === 404) {
        throw new ChatApiError(
          'CONVERSATION_NOT_FOUND',
          isProfile ? 'User not found or chat unavailable' : 'Activity not found or not active'
        );
      }

      // Legacy: spec uses HTTP 200 for "already exists"; keep 409 for older deployments
      if (response.status === 409) {
        console.log('✅ Conversation already exists (409), returning existing:', errorData.conversation_id);
        return errorData as unknown as CreateConversationResponse;
      }

      throw new ChatApiError(
        'CONNECTION_ERROR',
        errStr || `Failed to create conversation: ${response.status}`
      );
    }

    const data = await response.json();
    console.log('✅ Conversation created successfully:', data.conversation_id);
    return data;
  } catch (error) {
    console.error('Error creating conversation:', error);
    if (error instanceof ChatApiError) {
      throw error;
    }
    throw new ChatApiError('CONNECTION_ERROR', 'Network error while creating conversation');
  }
}

/**
 * Find an existing conversation with the given participant (Firebase UID), or null.
 */
export const findConversationWithParticipant = async (
  participantUserId: string
): Promise<string | null> => {
  try {
    const { conversations } = await getUserConversations();
    const found = conversations.find(
      (c) => c.other_participant?.user_id === participantUserId
    );
    return found?.conversation_id ?? null;
  } catch (error) {
    console.error('findConversationWithParticipant:', error);
    return null;
  }
};

/**
 * Get list of user's conversations
 */
export const getUserConversations = async (): Promise<ConversationsResponse> => {
  console.log('🔄 Fetching user conversations');
  
  try {
    const headers = await getAuthHeaders();
    if (!hasAuthorization(headers)) {
      throw new ChatApiError('ACCESS_DENIED', 'User not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/chat/conversations/`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new ChatApiError('CONNECTION_ERROR', `Failed to fetch conversations: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Conversations fetched successfully:', data.conversations?.length || 0);
    console.log('🔍 Conversations API response structure:', data);
    
    // According to backend documentation, the response should have a specific structure
    // Let's handle both old and new response formats for compatibility
    const conversations = data.conversations || [];
    const formattedResponse: ConversationsResponse = {
      conversations: conversations.map((conv: RawConversationData) => ({
        conversation_id: conv.firebase_conversation_id || conv.conversation_id,
        firebase_config: conv.firebase_config,
        conversation_type: conv.conversation_type,
        activity: conv.activity,
        other_participant: conv.other_participant,
        last_message: conv.last_message,
        unread_count: conv.unread_count,
        status: conv.status,
        user_role: conv.user_role || (conv.other_participant?.role === 'organizer' ? 'participant' : 'organizer'),
        created_at: conv.created_at
      })),
      firebase_config: data.firebase_config
    };
    
    return formattedResponse;

  } catch (error) {
    console.error('Error fetching conversations:', error);
    if (error instanceof ChatApiError) {
      throw error;
    }
    throw new ChatApiError('CONNECTION_ERROR', 'Network error while fetching conversations');
  }
};

/**
 * Get messages from a specific conversation
 */
export const getConversationMessages = async (
  firebaseConversationId: string,
  limit: number = 50,
  cursor?: string
): Promise<ConversationMessagesResponse> => {
  console.log('🔄 Fetching messages for conversation:', firebaseConversationId);
  
  try {
    const headers = await getAuthHeaders();
    if (!hasAuthorization(headers)) {
      throw new ChatApiError('ACCESS_DENIED', 'User not authenticated');
    }
    const url = new URL(`${API_BASE_URL}/api/chat/conversations/${firebaseConversationId}/messages/`);
    
    url.searchParams.append('limit', limit.toString());
    if (cursor) {
      url.searchParams.append('cursor', cursor);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      // Attempt to log server-provided error details to aid debugging (especially for 500)
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch {
        // ignore body read errors
      }
      if (response.status === 404) {
        throw new ChatApiError('CONVERSATION_NOT_FOUND', 'Conversation not found or access denied', firebaseConversationId);
      }
      // Safely detect presence of headers across all HeadersInit shapes
      let hasAuth = false;
      let hasApiKey = false;
      if (headers instanceof Headers) {
        hasAuth = headers.has('Authorization');
        hasApiKey = headers.has('X-API-KEY');
      } else if (Array.isArray(headers)) {
        const lower = (k: string) => k.toLowerCase();
        hasAuth = headers.some(([k]) => lower(k) === 'authorization');
        hasApiKey = headers.some(([k]) => lower(k) === 'x-api-key');
      } else {
        const rec = headers as Record<string, string>;
        hasAuth = !!rec['Authorization'];
        hasApiKey = !!rec['X-API-KEY'];
      }
      console.error('Chat messages fetch failed:', {
        status: response.status,
        url: url.toString(),
        hasAuth,
        hasApiKey,
        body: errorBody?.slice(0, 500) // cap log size
      });
      throw new ChatApiError('CONNECTION_ERROR', `Failed to fetch messages: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Loaded ${data.messages.length} messages for conversation`);
    return data;

  } catch (error) {
    console.error('Error fetching conversation messages:', error);
    if (error instanceof ChatApiError) {
      throw error;
    }
    throw new ChatApiError('CONNECTION_ERROR', 'Network error while fetching messages');
  }
};

/**
 * Send a message to a conversation
 */
export const sendMessage = async (
  conversationId: string,
  message: string
): Promise<SendMessageResponse> => {
  console.log('🔄 Sending message to conversation:', conversationId);
  
  try {
    const headers = await getAuthHeaders();
    if (!hasAuthorization(headers)) {
      throw new ChatApiError('ACCESS_DENIED', 'User not authenticated');
    }
    
    const requestBody: SendMessageRequest = {
      conversation_id: conversationId,
      message: message,
      message_type: 'text'
    };

    const response = await fetch(`${API_BASE_URL}/api/chat/send_message/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json();
      
      if (response.status === 404) {
        throw new ChatApiError('CONVERSATION_NOT_FOUND', 'Conversation not found or access denied', conversationId);
      }
      
      if (response.status === 400) {
        const errorMessage = errorData.message || 'Invalid message content';
        throw new ChatApiError('MESSAGE_SEND_FAILED', Array.isArray(errorMessage) ? errorMessage[0] : errorMessage);
      }

      throw new ChatApiError('MESSAGE_SEND_FAILED', `Failed to send message: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Message sent successfully:', data.firebase_message_id);
    return data;

  } catch (error) {
    console.error('Error sending message:', error);
    if (error instanceof ChatApiError) {
      throw error;
    }
    throw new ChatApiError('MESSAGE_SEND_FAILED', 'Network error while sending message');
  }
};

/**
 * Mark a conversation as read
 * Note: According to backend documentation, read receipts should be handled directly in Firebase
 * This function now just logs the action since Firebase handles read receipts automatically
 */
export const markConversationAsRead = async (conversationId: string): Promise<void> => {
  console.log('🔄 Marking conversation as read:', conversationId);
  
  try {
    // According to backend documentation, read receipts are handled directly in Firebase:
    // "Step 5: Mark as Read - Handle directly in Firebase:
    // Update conversations/{conversationId}/participants/{userId}/lastReadAt"
    
    // Firebase read receipts are handled automatically by the markAllMessagesAsRead function
    // in the firebaseChat service when messages are received/viewed
    
    console.log('✅ Conversation marked as read (handled by Firebase)');

  } catch (error) {
    console.error('Error marking conversation as read:', error);
    if (error instanceof ChatApiError) {
      throw error;
    }
    throw new ChatApiError('CONNECTION_ERROR', 'Network error while marking as read');
  }
};

/**
 * Get chat statistics for the current user
 */
export const getChatStats = async (): Promise<ChatStats> => {
  console.log('🔄 Fetching chat statistics');
  
  try {
    const headers = await getAuthHeaders();
    if (!hasAuthorization(headers)) {
      throw new ChatApiError('ACCESS_DENIED', 'User not authenticated');
    }
    
    // TODO: Backend endpoint not implemented yet, returning mock data
    console.log('⚠️ Chat stats endpoint not available, returning mock data');
    
    // Get conversation count from existing conversations
    const conversations = await getUserConversations();
    const mockStats: ChatStats = {
      total_conversations: conversations.conversations.length,
      active_conversations: conversations.conversations.filter(c => c.status === 'active').length,
      total_unread_messages: conversations.conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0),
      conversations_as_organizer: conversations.conversations.filter(c => c.user_role === 'organizer').length,
      conversations_as_participant: conversations.conversations.filter(c => c.user_role === 'participant').length,
    };
    
    console.log('✅ Chat statistics loaded (mock):', mockStats);
    return mockStats;

  } catch (error) {
    console.error('Error fetching chat statistics:', error);
    if (error instanceof ChatApiError) {
      throw error;
    }
    throw new ChatApiError('CONNECTION_ERROR', 'Network error while fetching chat stats');
  }
};

/**
 * Check if a conversation exists for a specific activity and current user
 * Note: This function is optimized to work with cached conversations in ChatContext
 */
export const checkConversationExists = async (activityId: number): Promise<string | null> => {
  try {
    const conversations = await getUserConversations();
    const existingConversation = conversations.conversations.find(
      conv => conv.activity?.id === activityId
    );
    
    return existingConversation ? existingConversation.conversation_id : null;
  } catch (error) {
    console.error('Error checking if conversation exists:', error);
    return null;
  }
};

/**
 * Get conversation by firebase conversation ID
 */
export const getConversationById = async (firebaseConversationId: string): Promise<ConversationListItem | null> => {
  try {
    const conversations = await getUserConversations();
    return conversations.conversations.find(
      conv => conv.conversation_id === firebaseConversationId
    ) || null;
  } catch (error) {
    console.error('Error getting conversation by ID:', error);
    return null;
  }
};

/**
 * Helper function to extract activity ID from firebase conversation ID
 */
export const extractActivityIdFromConversationId = (firebaseConversationId: string): number | null => {
  try {
    // Format: "activity_123_chat_user1_user2"
    const parts = firebaseConversationId.split('_');
    if (parts.length >= 2 && parts[0] === 'activity') {
      return parseInt(parts[1], 10);
    }
    return null;
  } catch (error) {
    console.error('Error extracting activity ID from conversation ID:', error);
    return null;
  }
};

/**
 * Helper function to format conversation display name
 */
export const getConversationDisplayName = (conversation: ConversationListItem, _currentUserId: string): string => {
  // Return the name of the other participant
  return conversation.other_participant.name;
};

/**
 * Helper function to get conversation status icon
 */
export const getConversationStatusIcon = (status: string): string => {
  switch (status) {
    case 'active':
      return '💬';
    case 'archived':
      return '📦';
    case 'closed':
      return '🔒';
    default:
      return '❓';
  }
};

/**
 * Helper function to format last message time
 */
export const formatLastMessageTime = (timestamp: string): string => {
  const messageDate = new Date(timestamp);
  const now = new Date();
  const diffInHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 1) {
    const diffInMinutes = Math.floor(diffInHours * 60);
    return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else if (diffInHours < 24 * 7) {
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  } else {
    return messageDate.toLocaleDateString();
  }
};
